import { ArrowRight, Quote, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { IMAGES, STORIES } from "@/data/content";
import KineticHero from "@/components/KineticHero";
import ImageReveal from "@/components/ImageReveal";
import MagneticButton from "@/components/MagneticButton";
import SplitText from "@/components/SplitText";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";

export default function SuccessStories() {
  return (
    <div data-testid="success-stories-page" className="overflow-x-clip">
      <KineticHero
        eyebrow="Success Stories"
        image={IMAGES.condoInfinity}
        lines={[
          "Goals turned",
          <span key="k" className="italic text-gl-gold">into keys in hand.</span>,
        ]}
        subtitle="Real-world outcomes from clients who reframed what was possible. (Placeholder stories — swap in your own.)"
      />

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {STORIES.map((s, i) => (
            <motion.article
              key={s.name}
              data-testid={`story-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-gl-gold/50 transition-colors duration-500"
            >
              <div className="relative h-72 overflow-hidden">
                <ImageReveal
                  src={s.image}
                  alt={s.location}
                  className="w-full h-full"
                  imgClassName="group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gl-bg2 to-transparent pointer-events-none" />
                <span className="absolute bottom-4 left-5 inline-flex items-center gap-2 text-gl-gold text-sm tracking-wide z-10">
                  <MapPin size={15} /> {s.location}
                </span>
              </div>
              <div className="p-8 lg:p-10">
                <Quote size={28} className="text-gl-gold/40 mb-4" />
                <p className="font-serif-display text-2xl text-gl-text font-light leading-snug italic">
                  {s.quote}
                </p>
                <div className="mt-6 flex items-center justify-between border-t border-gl-gold/15 pt-5">
                  <span className="text-gl-text2 font-light">{s.name}</span>
                  <span className="text-gl-gold text-sm tracking-wide">{s.result}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* RESULTS MARQUEE */}
      <section className="bg-gl-bg2 border-y border-gl-gold/15 py-8 md:py-12">
        <Marquee
          items={STORIES.map((s) => s.result)}
          duration={30}
          className="font-serif-display text-3xl md:text-5xl text-gl-text/90 font-light"
          itemClassName="italic"
        />
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.villaAlt} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gl-bg/88" />
          <div className="absolute inset-0 bg-gl-primary/25 mix-blend-multiply" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <SplitText
            as="h2"
            animateOnLoad={false}
            className="font-serif-display text-4xl md:text-6xl font-light text-gl-text leading-[1.05]"
            lines={["Your story", <span key="n" className="italic text-gl-gold">could be next.</span>]}
          />
          <Reveal delay={0.25}>
            <div className="mt-11 flex justify-center">
              <MagneticButton
                to="/contact"
                data-testid="stories-cta"
                className="px-9 py-4 bg-gl-gold text-gl-bg font-medium tracking-wide hover:bg-gl-goldHover transition-colors duration-300"
              >
                Start Your Strategy Call
                <ArrowRight size={18} />
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
